import { useMemo, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ArrowUpRight, Box, Code2, Cog, LogOut, Search } from "lucide-react";
import LabBrand from "./LabBrand";
import Thumbnail from "./Thumbnail";
import EmbedDialog from "./EmbedDialog";
import type { Model } from "../lib/api";
import { useAuth } from "../lib/auth";
import { COLLECTION_LABELS, groupBySection, type Collection } from "../lib/catalog";

const COLLECTION_PATHS: Record<Collection, string> = { anatomy: "/", embryology: "/embryology" };

/** Where a model's own page should send the student back to. */
export function libraryPath(collection: Collection | undefined) {
  return COLLECTION_PATHS[collection ?? "anatomy"];
}

export function LibraryHeader() {
  const { user, logout } = useAuth();
  return <header className="lab-header">
    <Link to="/" aria-label="Virtual Anatomy Lab home"><LabBrand /></Link>
    <nav className="flex items-center gap-2">
      {user?.role === "admin" && <Link to="/admin" aria-label="Teaching studio" className="btn-secondary text-xs"><Cog size={15} /><span className="hidden sm:inline">Teaching studio</span></Link>}
      {user?.auth_disabled ? <span className="chip">Development</span> : <button onClick={logout} className="btn-ghost text-xs" title="Lock lab"><LogOut size={15} /><span className="hidden sm:inline">Lock lab</span></button>}
    </nav>
  </header>;
}

/** The site's two collections, as a switch above each library's title. */
export function CollectionTabs() {
  return <nav aria-label="Collections" className="mode-switch mb-6 w-full sm:w-fit">
    {(Object.keys(COLLECTION_PATHS) as Collection[]).map((c) => <NavLink key={c} to={COLLECTION_PATHS[c]} end className={({ isActive }) => `mode-button sm:flex-none sm:px-6 ${isActive ? "mode-button-active" : ""}`}>{COLLECTION_LABELS[c]}</NavLink>)}
  </nav>;
}

export function SearchBox({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  return <div className="relative w-full sm:max-w-xs"><Search size={16} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" /><input aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} placeholder="Search the library…" className="input pl-10" /></div>;
}

export function matchesSearch(model: Pick<Model, "name" | "description">, search: string) {
  const q = search.trim().toLowerCase();
  return !q || model.name.toLowerCase().includes(q) || (model.description ?? "").toLowerCase().includes(q);
}

/**
 * Individual scans under one divider per section, with a row of jump links
 * when there's more than one section to move between.
 */
export function ModelSections({ collection, models }: { collection: Collection; models: Model[] }) {
  const groups = useMemo(() => groupBySection(collection, models), [collection, models]);
  const [embedFor, setEmbedFor] = useState<Model | null>(null);
  return <>
    {groups.length > 1 && <nav aria-label="Jump to section" className="-mt-2 mb-8 flex flex-wrap gap-2">
      {groups.map((g) => <a key={g.key} href={`#section-${g.key}`} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-brand-300 hover:text-brand-800">{g.label}<span className="tabular-nums text-slate-400">{g.items.length}</span></a>)}
    </nav>}
    {groups.map((g) => <section key={g.key} id={`section-${g.key}`} aria-labelledby={`section-${g.key}-title`} className="mb-10 scroll-mt-6 last:mb-0">
      <div className="mb-4 flex items-center gap-3"><h3 id={`section-${g.key}-title`} className="section-title">{g.label}</h3><span className="chip">{g.items.length}</span><div className="h-px flex-1 bg-slate-200/70" /></div>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{g.items.map((model) => <ModelCard key={model.id} model={model} onEmbed={() => setEmbedFor(model)} />)}</div>
    </section>)}
    {embedFor && <EmbedDialog modelId={embedFor.id} modelName={embedFor.name} onClose={() => setEmbedFor(null)} />}
  </>;
}

function ModelCard({ model, onEmbed }: { model: Model; onEmbed: () => void }) {
  return <article className="card group overflow-hidden transition-colors hover:border-brand-300">
    <Link to={`/embed/${model.id}`} className="relative block"><Thumbnail model={model} className="aspect-[4/3] w-full" /><span className="absolute bottom-3 right-3 flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-brand-700 transition-transform group-hover:-translate-y-0.5"><ArrowUpRight size={16} /></span></Link>
    <div className="p-5"><div className="flex items-center justify-between gap-2"><Link to={`/embed/${model.id}`} className="truncate font-semibold text-slate-800 hover:text-brand-700">{model.name}</Link><button onClick={onEmbed} className="btn-icon h-7 w-7 shrink-0" title={`Embed ${model.name}`}><Code2 size={14} /></button></div>{model.description && <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-500">{model.description}</p>}<div className="mt-4 flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-slate-400"><Box size={12} />3D scan<span className="ml-auto normal-case tracking-normal">{(model.file_size / (1024 * 1024)).toFixed(1)} MB</span></div></div>
  </article>;
}

export function LibraryFooter() {
  return <footer className="mt-14 flex justify-between border-t border-slate-200/80 pt-6 text-[10px] text-slate-400"><span>UNSW · Virtual Anatomy Lab</span><span>Made for curious minds.</span></footer>;
}

export function LoadingGrid() {
  return <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" aria-label="Loading library">{[1, 2, 3].map((i) => <div key={i} className="card overflow-hidden"><div className="skeleton aspect-[4/3] rounded-none" /><div className="p-5"><div className="skeleton h-5 w-2/3" /><div className="skeleton mt-3 h-3 w-1/2" /></div></div>)}</div>;
}
